/**
 * Bank Accounts Reader Audit diagnostic card.
 *
 * Enumerates every db.bank_accounts query site and reports whether it
 * uses the canonical hidden-filter (VISIBLE_BANK_ACCOUNT_FILTER) or
 * carries the intentional-inclusion marker. Surfaces predicate-duplication
 * drift so archived accounts cannot silently reappear in financial totals.
 */

import { useState } from 'react';
import { Loader2, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function ActionBadge({ action }) {
  if (action === 'OK') {
    return (
      <span className="inline-flex items-center gap-1 text-green-700">
        <CheckCircle2 className="h-3 w-3" />
        <span className="text-xs">filtered</span>
      </span>
    );
  }
  if (action === 'OK (intentional)') {
    return (
      <span className="inline-flex items-center gap-1 text-blue-700">
        <CheckCircle2 className="h-3 w-3" />
        <span className="text-xs">intentional</span>
      </span>
    );
  }
  if (action === 'NEEDS MIGRATION') {
    return (
      <span className="inline-flex items-center gap-1 text-red-700">
        <XCircle className="h-3 w-3" />
        <span className="text-xs">needs migration</span>
      </span>
    );
  }
  if (action === 'NEEDS MARKER') {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700">
        <AlertTriangle className="h-3 w-3" />
        <span className="text-xs">needs marker</span>
      </span>
    );
  }
  if (action === 'NEEDS FAN DECISION') {
    return (
      <span className="inline-flex items-center gap-1 text-purple-700">
        <AlertTriangle className="h-3 w-3" />
        <span className="text-xs">needs decision</span>
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{action}</span>;
}

function ClassBadge({ classification }) {
  var colorMap = {
    'financial-total': 'bg-red-100 text-red-800',
    'operational': 'bg-amber-100 text-amber-800',
    'admin-audit': 'bg-blue-100 text-blue-800',
  };
  return (
    <Badge variant="outline" className={'text-[10px] px-1.5 py-0 ' + (colorMap[classification] || '')}>
      {classification}
    </Badge>
  );
}

export function BankAccountsReaderAuditPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);

  var runAudit = async function () {
    setLoading(true);
    setData(null);
    try {
      var res = await diagnosticsAPI.bankAccountsReaderAudit();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          {data && data.verdict === 'healthy' ? (
            <ShieldCheck className="h-5 w-5 text-green-600" />
          ) : data ? (
            <ShieldAlert className="h-5 w-5 text-red-600" />
          ) : (
            <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          )}
          Bank Accounts Reader Audit
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Audits every db.bank_accounts query site for hidden-filter compliance.
          Financial-total and operational readers must use the canonical
          VISIBLE_BANK_ACCOUNT_FILTER. Admin/audit readers must carry the
          intentional-inclusion marker.
        </p>

        <Button onClick={runAudit} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <ShieldCheck className="h-4 w-4 mr-2" />
          )}
          Run Audit
        </Button>

        {data && (
          <div className="space-y-3">
            {/* Verdict */}
            <div className={
              'rounded-md px-3 py-2 text-sm font-medium ' +
              (data.verdict === 'healthy'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200')
            }>
              {data.verdict === 'healthy'
                ? 'All readers are correctly filtered or intentionally unfiltered.'
                : 'Some readers need migration or markers. See details below.'}
            </div>

            {/* Summary */}
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">{data.summary.total_sites} sites</Badge>
              <Badge variant="outline" className="text-green-700">{data.summary.ok} OK</Badge>
              {data.summary.needs_migration > 0 && (
                <Badge variant="destructive">{data.summary.needs_migration} needs migration</Badge>
              )}
              {data.summary.needs_marker > 0 && (
                <Badge variant="outline" className="text-amber-700">{data.summary.needs_marker} needs marker</Badge>
              )}
              {data.summary.needs_decision > 0 && (
                <Badge variant="outline" className="text-purple-700">{data.summary.needs_decision} needs decision</Badge>
              )}
            </div>

            {/* Results table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1.5 pr-3 font-medium">Surface</th>
                    <th className="py-1.5 pr-3 font-medium">File</th>
                    <th className="py-1.5 pr-3 font-medium">Class</th>
                    <th className="py-1.5 pr-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(function (row, i) {
                    return (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-1.5 pr-3 text-xs">{row.surface}</td>
                        <td className="py-1.5 pr-3 text-xs font-mono text-muted-foreground">
                          {row.file}{row.line ? ':' + row.line : ''}
                        </td>
                        <td className="py-1.5 pr-3">
                          <ClassBadge classification={row.classification} />
                        </td>
                        <td className="py-1.5 pr-3">
                          <ActionBadge action={row.action} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
